import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function CitiesAdmin() {
  const qc = useQueryClient();
  const { data: cities = [], isLoading } = useQuery({ queryKey: ["cities"], queryFn: () => api.get("/cities").then((r) => r.data) });
  const [editing, setEditing] = useState(null);

  return (
    <div data-testid="admin-cities">
      <h1 className="text-2xl font-heading font-bold text-navy-heading mb-1">City SEO</h1>
      <p className="text-slate-500 text-sm mb-6">Edit SEO content for city taxi pages.</p>
      {isLoading ? <div className="text-slate-400">Loading…</div> :
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cities.map((c) => (
            <button key={c.id} onClick={() => setEditing(c)} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 text-left hover:border-brand transition-colors" data-testid={`city-edit-${c.slug}`}>
              <img src={c.image} alt={c.name} className="h-24 w-full object-cover rounded-xl mb-3" />
              <div className="font-heading font-bold text-navy-heading">{c.name}</div>
              <div className="text-xs text-slate-500 mt-1 line-clamp-2">{c.meta_title}</div>
            </button>
          ))}
        </div>}
      {editing && <CityModal city={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["cities"] }); }} />}
    </div>
  );
}

function CityModal({ city, onClose, onSaved }) {
  const [form, setForm] = useState({ h1: city.h1, meta_title: city.meta_title, meta_description: city.meta_description, intro: (city.intro || []).join("\n\n") });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/cities/${city.id}`, { h1: form.h1, meta_title: form.meta_title, meta_description: form.meta_description, intro: form.intro.split("\n\n").map((x) => x.trim()).filter(Boolean) });
      toast.success("City updated"); onSaved();
    } catch { toast.error("Save failed"); } finally { setLoading(false); }
  };
  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="font-heading font-bold text-navy-heading text-lg mb-4">Edit {city.name} SEO</h2>
        <div className="space-y-4">
          <div><label className="block text-xs font-semibold text-navy-heading mb-1.5">H1 Heading</label><input className={inp} value={form.h1} onChange={set("h1")} /></div>
          <div><label className="block text-xs font-semibold text-navy-heading mb-1.5">Meta Title</label><input className={inp} value={form.meta_title} onChange={set("meta_title")} /></div>
          <div><label className="block text-xs font-semibold text-navy-heading mb-1.5">Meta Description</label><textarea rows={2} className={inp} value={form.meta_description} onChange={set("meta_description")} /></div>
          <div><label className="block text-xs font-semibold text-navy-heading mb-1.5">Intro Content (separate paragraphs with a blank line)</label><textarea rows={6} className={inp} value={form.intro} onChange={set("intro")} /></div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 border border-slate-200 text-slate-600 font-semibold py-3 rounded-xl">Cancel</button>
          <button onClick={save} disabled={loading} className="flex-1 flex items-center justify-center gap-2 bg-brand text-white font-semibold py-3 rounded-xl">{loading && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
        </div>
      </div>
    </div>
  );
}
